import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import { LoadingStateService, LoadingState } from './shared/services/layout/loading-state.service';
import { AppInitializationService } from './core/auth/services/app-initialization.service';
import { AuthService } from './core/auth/services/auth.service';
import { HeaderComponent } from './layout/header/header.component';
import { FooterComponent } from './layout/footer/footer.component';
import { NgxSpinnerModule } from 'ngx-spinner';
import { ToastModule } from 'primeng/toast';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, RouterOutlet, HeaderComponent, FooterComponent, NgxSpinnerModule, ToastModule],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'Syngenta | Premix Calculator';

  // App state properties - now managed by initialization service
  isTokenInitialized = false;
  isAuthCheckComplete = false;
  isAuthenticated = false;
  isCallbackLoading = false;
  loadingMessage = 'Verifying access...';
  currentUserName = '';

  globalLoadingState: LoadingState = { isLoading: false, message: '', type: 'general' };
  private destroy$ = new Subject<void>();

  constructor(
    private appInitializationService: AppInitializationService,
    private authService: AuthService,
    private loadingStateService: LoadingStateService
  ) {
    this.initializeApp();
    this.subscribeToLoadingState();
    this.subscribeToAppState();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit() {
    // Subscribe to user profile changes
    this.authService.userProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe(userProfile => {
        if (userProfile) {
          this.appInitializationService.updateUserName(userProfile);
        }
      });
  }

  /**
   * Subscribe to loading state changes
   */
  private subscribeToLoadingState(): void {
    this.loadingStateService.loading$
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        this.globalLoadingState = state;
      });
  }

  /**
   * Subscribe to app initialization state changes
   */
  private subscribeToAppState(): void {
    this.appInitializationService.state$
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        this.isTokenInitialized = state.isTokenInitialized;
        this.isAuthCheckComplete = state.isAuthCheckComplete;
        this.isAuthenticated = state.isAuthenticated;
        this.isCallbackLoading = state.isCallbackLoading;
        this.loadingMessage = state.loadingMessage;
        this.currentUserName = state.currentUserName;
      });
  }

  /**
   * Initialize the application
   */
  private async initializeApp(): Promise<void> {
    await this.appInitializationService.initializeApp();
  }

  /**
   * Handle user login - delegated to initialization service
   */
  async login() {
    await this.appInitializationService.login();
  }
}
